import { Table } from 'antd';
import React, { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getShareManagementColumns } from './columns';
import { SharedLink, SharedLinksResponse, SharedLinkAPI } from './types';
import api from 'api';
import TableLoader from 'components/Sceletons/TableLoader';
import { useAppSelector } from 'store/hook';

export default function ShareManagementTable({ token }: { token: string }) {
  const { t } = useTranslation();
  const [sharedLinks, setSharedLinks] = React.useState<SharedLink[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);

   const { messageApi } = useAppSelector((state) => state.windowState);
  
  useEffect(() => {
    if (token) {
      fetchSharedLinks();
    }
  }, [token, page]);

  const fetchSharedLinks = async () => {
    setLoading(true);
    try {
      const response = await api.getSharedLinks({
        filters: [{ field: 'token', value: token }],
        page: page,
        per_page: 10,
      });

      const data: SharedLinksResponse = response.data;
      const transformedData: SharedLink[] = data.temp_links.map(
        (link: SharedLinkAPI) => ({
          key: link.id,
          userName: link.full_name,
          email: link.addressee_email,
          status:
            link.expired ||
            link.expires_limit === 0 ||
            (link.expires_at && new Date(link.expires_at) <= new Date())
              ? 'expired'
              : 'active',
          actions: '',
          rawLink: link,
        })
      );
      setSharedLinks(transformedData);
      setTotal(data.pagination.total || 0);
    } catch (error) {
      messageApi?.error(t('Documents.modals.share.management.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = useCallback(async (linkId: string) => {
    try {
      await api.revokeShare(linkId);
      messageApi?.success(t('Documents.modals.share.management.revokeSuccess'));
      fetchSharedLinks();
    } catch (error) {
      messageApi?.error(t('Documents.modals.share.management.revokeError'));
    }
  }, [t, messageApi]);

  const handleCopyLink = useCallback((link: SharedLinkAPI) => {
    const permissions = link.permissions ? link.permissions.join('%2C') : '';
    const email = link.addressee_email || 'None';
    const passwordActive = link.password ? 'true' : 'false';
    const ext = link.ext || '';
    
    const copyUrl = `${window.location.origin}/temp_link/${link.id}/?ext=${ext}&permissions=${permissions}&email=${email}&password_active=${passwordActive}`;
    
    navigator.clipboard.writeText(copyUrl);
    messageApi?.success(t('Documents.modals.share.linkCopied'));
  }, [messageApi, t]);

  const columns = useMemo(() => getShareManagementColumns(t, handleRevoke, handleCopyLink), [t, handleRevoke, handleCopyLink]);
  
  if (loading) {
    return <TableLoader height={300} />;
  }

  return (
    <div>
      <Table
        columns={columns}
        dataSource={sharedLinks}
        loading={loading}
        pagination={{
          current: page,
          pageSize: 10,
          showSizeChanger: false,
          showQuickJumper: false,
          onChange: (page) => setPage(page),
          total: total,
        }}
        size='middle'
        locale={{
          emptyText: t('Documents.modals.share.management.noData'),
        }}
      />
    </div>
  );
}
